import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, MenuIcon, RadioIcon, XIcon } from 'lucide-react';
import { ProductionBoards } from '../components/ProductionBoards';
import { Look, RunwayOrder } from '../components/RunwayOrder';
import { VipSeating } from '../components/VipSeating';
import { useScreenInit } from '../useScreenInit.js';
const initialLooks: Look[] = [{
  id: 1,
  name: 'The Overture',
  model: 'Awar Odhiang',
  image: 'https://images.unsplash.com/photo-1539109136881-3be0616acf4b?auto=format&fit=crop&w=800&q=85',
  status: 'approved',
  note: 'No jewellery. Let the shoulder speak.'
}, {
  id: 2,
  name: 'Nocturne',
  model: 'Mona Tougaard',
  image: 'https://images.unsplash.com/photo-1566174053879-31528523f8ae?auto=format&fit=crop&w=800&q=85',
  status: 'approved',
  note: 'Longer pause at runway head.'
}, {
  id: 3,
  name: 'Soft Armour',
  model: 'Amar Akway',
  image: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?auto=format&fit=crop&w=800&q=85',
  status: 'review',
  note: 'Review glove with styling at 15:00.'
}, {
  id: 4,
  name: 'Black Iris',
  model: 'Loli Bahia',
  image: 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?auto=format&fit=crop&w=800&q=85',
  status: 'approved',
  note: 'Perfect as fitted. Do not overwork.'
}, {
  id: 5,
  name: 'Oxblood',
  model: 'Anok Yai',
  image: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=800&q=85',
  status: 'alterations',
  note: 'Raise hem 12mm. Final fitting tonight.'
}, {
  id: 6,
  name: 'Column I',
  model: 'Adut Akech',
  image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&w=800&q=85',
  status: 'review',
  note: 'Test movement under hard light.'
}, {
  id: 7,
  name: 'Afterimage',
  model: 'Sacha Quenby',
  image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=800&q=85',
  status: 'approved',
  note: 'Walk close to the house left edge.'
}, {
  id: 8,
  name: 'Finale',
  model: 'Mariam de Vinzelle',
  image: 'https://images.unsplash.com/photo-1566206091558-7f218b696731?auto=format&fit=crop&w=800&q=85',
  status: 'approved',
  note: 'Hold. Blackout. Then music out.'
}];
const nav = [['runway', 'Runway'], ['approvals', 'Approvals'], ['fittings', 'Fittings'], ['samples', 'Samples'], ['venue', 'Venue'], ['seating', 'VIP Seating']];
export function FashionWeekWorkspace() {
  useScreenInit();
  const [looks, setLooks] = useState(initialLooks);
  const [menuOpen, setMenuOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  function reorderLook(id: number, direction: -1 | 1) {
    setLooks(current => {
      const index = current.findIndex(look => look.id === id);
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1600);
  }
  function navigate(id: string) {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    setMenuOpen(false);
  }
  return <main className="min-h-screen w-full overflow-hidden bg-[#f4efe4] text-[#11100e]" data-id="element-159">
      <header className="sticky top-0 z-40 border-b border-black bg-[#f4efe4]/95 backdrop-blur-sm" data-id="element-160">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3 sm:px-7" data-id="element-161">
          <button type="button" onClick={() => navigate('top')} className="focus-ring font-serif text-xl font-bold tracking-tight" data-id="element-162">
            MAISON / 26
          </button>
          <nav className="hidden items-center gap-6 lg:flex" aria-label="Workspace sections" data-id="element-163">
            {nav.map(([id, label]) => <button key={id} type="button" onClick={() => navigate(id)} className="focus-ring text-[10px] font-bold uppercase tracking-[.16em] hover:text-[#c51f2d]" data-id="element-164">
                {label}
              </button>)}
          </nav>
          <div className="flex items-center gap-3" data-id="element-165">
            <span className="hidden items-center gap-2 text-[10px] font-bold uppercase tracking-[.15em] sm:flex" data-id="element-166">
              <RadioIcon className="h-3.5 w-3.5 text-[#c51f2d]" data-id="element-167" /> Live
              production
            </span>
            <button type="button" onClick={() => setMenuOpen(!menuOpen)} className="focus-ring grid h-9 w-9 place-items-center border border-black lg:hidden" aria-expanded={menuOpen} aria-label="Toggle navigation" data-id="element-168">
              {menuOpen ? <XIcon className="h-4 w-4" data-id="element-169" /> : <MenuIcon className="h-4 w-4" data-id="element-170" />}
            </button>
          </div>
        </div>
        {menuOpen && <motion.nav initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} className="border-t border-black bg-[#f4efe4] px-4 py-3 lg:hidden" aria-label="Mobile workspace sections" data-id="element-171">
            {nav.map(([id, label]) => <button key={id} type="button" onClick={() => navigate(id)} className="focus-ring block w-full border-b border-black/30 py-3 text-left font-serif text-2xl italic" data-id="element-172">
                {label}
              </button>)}
          </motion.nav>}
      </header>

      <div id="top" className="mx-auto max-w-[1600px] px-4 sm:px-7" data-id="element-173">
        <section className="relative pb-12 pt-10 sm:pb-20 sm:pt-16" aria-labelledby="page-title" data-id="element-174">
          <div className="grid gap-8 lg:grid-cols-[1fr_230px] lg:items-end" data-id="element-175">
            <div data-id="element-176">
              <p className="utility-label mb-4" data-id="element-177">
                PARIS · FALL / WINTER 2026 · SHOW OFFICE
              </p>
              <motion.h1 id="page-title" initial={{
              y: 25,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} className="editorial-title max-w-6xl text-[clamp(4.5rem,13vw,13rem)]" data-id="element-178">
                FINAL
                <br data-id="element-179" />
                <span className="ml-[9vw] italic text-[#c51f2d]" data-id="element-180">CALL.</span>
              </motion.h1>
            </div>
            <aside className="border-t border-black pt-4 lg:mb-5" data-id="element-181">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.14em]" data-id="element-182">
                <CalendarDaysIcon className="h-4 w-4" data-id="element-183" /> Thursday, 05 March
              </div>
              <p className="mt-3 font-serif text-3xl" data-id="element-184">19:45 CET</p>
              <p className="mt-2 text-xs leading-relaxed" data-id="element-185">
                Palais de Tokyo
                <br data-id="element-186" />
                13 avenue du Président Wilson
              </p>
            </aside>
          </div>
          <p className="handwritten absolute bottom-4 left-[48%] hidden rotate-[-5deg] text-3xl text-[#c51f2d] md:block" data-id="element-187">
            breathe — we know the work.
          </p>
        </section>

        <RunwayOrder looks={looks} onReorder={reorderLook} data-id="element-188" />
        <ProductionBoards data-id="element-189" />
        <VipSeating data-id="element-190" />

        <footer className="flex flex-col gap-3 border-t border-black py-6 text-[10px] font-bold uppercase tracking-[.15em] sm:flex-row sm:justify-between" data-id="element-191">
          <span data-id="element-192">Maison production office / confidential</span>
          <span data-id="element-193">Last operational review · 14:32 CET</span>
        </footer>
      </div>

      <AnimateSave visible={saved} data-id="element-194" />
    </main>;
}
function AnimateSave({
  visible
}: {
  visible: boolean;
}) {
  return <motion.div initial={false} animate={{
    y: visible ? 0 : 80,
    opacity: visible ? 1 : 0
  }} className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 bg-black px-4 py-3 text-xs font-bold uppercase tracking-[.14em] text-white shadow-xl" role="status" aria-live="polite" data-id="element-195">
      Runway order updated
    </motion.div>;
}